import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../data.js';
import { useRecords, PageHead, Empty, money, shortDate, Drawer, Text, Area, Pick, Err } from '../ui.js';
import { CreateMenu } from './CreateMenu.js';
import { ApiError } from '../api.js';
import type { Deal } from '../api.js';

// Deals and the pipeline.
//
// The Accounts board tracks a PROSPECTING stage — identified, warming, engaged.
// This tracks money. They are different questions and a single board cannot
// answer both: an account can be "engaged" with no deal at all, and a deal can
// be in negotiation while the account sits in a stage nobody updated.

const STAGES = [
  { key: 'scoping', label: 'Scoping', p: 10 },
  { key: 'discovery', label: 'Discovery', p: 25 },
  { key: 'proposal', label: 'Proposal', p: 50 },
  { key: 'negotiation', label: 'Negotiation', p: 70 },
  { key: 'verbal', label: 'Verbal', p: 90 },
  { key: 'won', label: 'Won', p: 100 },
  { key: 'lost', label: 'Lost', p: 0 },
] as const;

type Row = Deal & { id: string; company_id: string; next_step: string | null; probability: number | null };

/**
 * Engagement types are the workspace's own offerings — what THIS operator
 * sells, defined at onboarding. Hardcoding Hamer's three lines here meant
 * every other workspace filed deals under someone else's service catalogue.
 * Kept as a fallback for a workspace that has not defined offerings yet.
 */
const FALLBACK_TYPES = ['Retainer', 'Project', 'Advisory'] as const;

const num = (v: unknown) => (v === null || v === undefined ? 0 : Number(v) || 0);

/** Default probability from the stage when nobody has set one by hand. */
const weightOf = (d: Row) =>
  d.probability ?? STAGES.find((s) => s.key === d.stage)?.p ?? 0;

/**
 * The people on the deal — the association every CRM treats as table stakes.
 *
 * deal_contacts existed in the schema and the records API from the start;
 * ContactRecord could link FROM the person's side; nothing ever showed the
 * deal's side. A deal with money and a close date but no names on it cannot
 * answer "who are we actually negotiating with".
 */
function DealPeople({ deal }: { deal: Row }) {
  const links = useRecords<{ id: string; contact_id: string; role: string | null }>(
    'deal_contacts', { deal_id: deal.id });
  const people = useRecords<{ id: string; name: string; title: string | null }>(
    'contacts', { company_id: deal.company_id });
  const [err, setErr] = useState<string | null>(null);

  const linked = new Set((links.rows ?? []).map((l) => l.contact_id));
  const nameOf = new Map((people.rows ?? []).map((p) => [p.id, p]));
  const addable = (people.rows ?? []).filter((p) => !linked.has(p.id));

  return (
    <section>
      <h4>People on this deal
        <span className="count">{links.rows?.length ?? 0}</span>
      </h4>
      {(links.rows ?? []).map((l) => {
        const p = nameOf.get(l.contact_id);
        return (
          <div className="person" key={l.id}>
            <div>
              <Link to={`/contacts/${l.contact_id}`}>
                <strong>{p?.name ?? 'someone from another account'}</strong>
              </Link>
              <span className="meta">{l.role ?? p?.title ?? ''}</span>
            </div>
            <button className="mini" onClick={() =>
              void links.remove(l.id).catch((e) => setErr(String(e)))}>
              Remove
            </button>
          </div>
        );
      })}
      {addable.length > 0 && (
        <div className="actions">
          <select defaultValue="" onChange={(e) => {
            if (!e.target.value) return;
            const v = e.target.value; e.target.value = '';
            void links.create({ deal_id: deal.id, contact_id: v })
              .catch((er) => setErr(String(er)));
          }}>
            <option value="">Add someone from the account…</option>
            {addable.map((p) => (
              <option key={p.id} value={p.id}>{p.name}{p.title ? ` — ${p.title}` : ''}</option>
            ))}
          </select>
        </div>
      )}
      {err && <p className="error">{err}</p>}
    </section>
  );
}

function EditDeal({ deal, types, onClose, onSave }: {
  deal: Row; types: string[]; onClose: () => void;
  onSave: (d: Record<string, unknown>) => Promise<unknown>;
}) {
  const [f, setF] = useState<Record<string, string>>({
    name: deal.name, stage: deal.stage, amount: String(deal.amount ?? ''),
    expected_close: deal.expected_close?.slice(0, 10) ?? '',
    next_step: deal.next_step ?? '', notes: deal.notes ?? '',
    close_reason: (deal as unknown as { close_reason?: string }).close_reason ?? '',
    probability: String(deal.probability ?? ''),
    engagement_type: (deal as unknown as { engagement_type?: string }).engagement_type ?? '',
    term_months: String((deal as unknown as { term_months?: number }).term_months ?? ''),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));

  return (
    <Drawer title={deal.name} subtitle="Deal" onClose={onClose} footer={
      <button className="primary" disabled={saving} onClick={async () => {
        setSaving(true); setError(null);
        try {
          await onSave({
            name: f.name, stage: f.stage,
            amount: f.amount ? Number(f.amount) : null,
            probability: f.probability ? Number(f.probability) : null,
            expected_close: f.expected_close || null,
            next_step: f.next_step || null, notes: f.notes || null,
            close_reason: f.close_reason || null,
            // Landing in won/lost through the drawer stamps the close the
            // same way the board drop does; leaving a close stage un-stamps.
            ...(f.stage === 'won' || f.stage === 'lost'
              ? { closed_at: new Date().toISOString().slice(0, 10), outcome: f.stage }
              : { closed_at: null, outcome: null }),
            engagement_type: f.engagement_type || null,
            term_months: f.term_months ? Number(f.term_months) : null,
          });
          onClose();
        } catch (e) {
          setError(e instanceof ApiError ? e.message : String(e)); setSaving(false);
        }
      }}>{saving ? 'Saving…' : 'Save'}</button>
    }>
      <Err>{error}</Err>
      <Text label="Name" value={f.name} onChange={(e) => set('name', e.target.value)} />
      <Pick label="Stage" value={f.stage} allowEmpty={false}
        options={STAGES.map((s) => ({ value: s.key, label: s.label }))}
        onChange={(v) => set('stage', v)} />
      <div className="pair">
        <Text label="Amount" type="number" value={f.amount}
          onChange={(e) => set('amount', e.target.value)} />
        <Text label="Probability %" type="number" value={f.probability}
          hint="Blank uses the stage default." onChange={(e) => set('probability', e.target.value)} />
      </div>
      {/* The workspace's service lines. A deal that records only a number
          cannot answer "what do we actually sell" six months from now. A saved
          value that is no longer an active offering stays selectable — editing
          an old deal must not silently blank its type. */}
      <Pick label="Engagement type" value={f.engagement_type}
        options={[...new Set([
          ...types,
          ...(f.engagement_type ? [f.engagement_type] : []),
        ])].map((v) => ({ value: v, label: v }))}
        onChange={(v) => set('engagement_type', v)} />
      <div className="pair">
        <Text label="Expected close" type="date" value={f.expected_close}
          onChange={(e) => set('expected_close', e.target.value)} />
        <Text label="Term (months)" type="number" value={f.term_months}
          hint="Retainers only. Blank for one-off work."
          onChange={(e) => set('term_months', e.target.value)} />
      </div>
      <Text label="Next step" value={f.next_step}
        onChange={(e) => set('next_step', e.target.value)} />
      {(f.stage === 'lost' || f.stage === 'won') && (
        <Text label={f.stage === 'lost' ? 'Why it was lost' : 'Why it was won'}
          value={f.close_reason}
          hint="Six months from now this line is the only part anyone reads."
          onChange={(e) => set('close_reason', e.target.value)} />
      )}
      <Area label="Notes" rows={4} value={f.notes} onChange={(v) => set('notes', v)} />
      <DealPeople deal={deal} />
    </Drawer>
  );
}

export function Deals() {
  const { companies } = useData();
  const { rows, update, reload } = useRecords<Row>('deals');
  const offerings = useRecords<{ id: string; name: string; is_active: boolean }>('offerings');
  const [view, setView] = useState<'board' | 'table'>('board');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Drag a card to a stage — the interaction every CRM board teaches. Same
   * write as the drawer's dropdown; the drawer remains for everything else.
   *
   * WON AND LOST ARE CLOSES, NOT COLUMNS. Landing on either stamps
   * closed_at and the outcome — the fields the schema always had and no
   * control ever set. A loss additionally opens the drawer, because a lost
   * deal without a reason is a report that can never explain itself.
   */
  async function moveTo(dealId: string, stage: string) {
    const deal = (rows ?? []).find((d) => d.id === dealId);
    if (!deal || deal.stage === stage) return;
    setError(null);
    const closing = stage === 'won' || stage === 'lost';
    try {
      await update(dealId, {
        stage,
        ...(closing
          ? { closed_at: new Date().toISOString().slice(0, 10), outcome: stage }
          : { closed_at: null, outcome: null }),
      });
      if (stage === 'lost') setEditing({ ...deal, stage });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }

  const engagementTypes = useMemo(() => {
    const active = (offerings.rows ?? []).filter((o) => o.is_active).map((o) => o.name);
    return active.length ? active : [...FALLBACK_TYPES];
  }, [offerings.rows]);

  const nameOf = useMemo(() => {
    const m = new Map<string, { name: string; slug: string }>();
    for (const c of companies ?? []) m.set(c.id, { name: c.name, slug: c.slug });
    return m;
  }, [companies]);

  const open = (rows ?? []).filter((d) => d.stage !== 'won' && d.stage !== 'lost');
  const totalOpen = open.reduce((s, d) => s + num(d.amount), 0);
  const weighted = open.reduce((s, d) => s + num(d.amount) * (weightOf(d) / 100), 0);
  const won = (rows ?? []).filter((d) => d.stage === 'won');

  if (!rows) return <p className="loading">Loading…</p>;

  return (
    <>
      <PageHead
        title="Deals" count={rows.length}
        sub="Money against accounts. The Accounts board tracks prospecting; this tracks revenue."
        action={<button className="primary" onClick={() => setCreating(true)}>New deal</button>}
      />

      <Err>{error}</Err>
      <div className="stats">
        <div className="stat"><span>Open</span><strong>{open.length}</strong></div>
        <div className="stat"><span>Open value</span><strong>{money(totalOpen)}</strong></div>
        <div className="stat"><span>Weighted</span><strong>{money(weighted)}</strong></div>
        <div className="stat"><span>Won</span><strong>
          {money(won.reduce((s, d) => s + num(d.amount), 0))}
        </strong></div>
      </div>

      {rows.length === 0 ? (
        <Empty
          title="No deals yet"
          hint="A deal is the point where an account stops being outreach and starts being revenue. Open one when there is real money on the table."
          action={<button className="primary" onClick={() => setCreating(true)}>Open a deal</button>}
        />
      ) : (
        <>
          <div className="toolbar">
            <div className="toggle">
              <button className={view === 'board' ? 'on' : ''} onClick={() => setView('board')}>Board</button>
              <button className={view === 'table' ? 'on' : ''} onClick={() => setView('table')}>Table</button>
            </div>
          </div>

          {view === 'board' ? (
            <div className="board">
              {/* While a drag is live, EVERY stage renders — a card must be
                  droppable on Proposal even when Proposal is empty, and the
                  empty columns disappear again the moment the drag ends. */}
              {STAGES.map((s) => {
                const inStage = rows.filter((d) => d.stage === s.key);
                if (!inStage.length && !dragging) return null;
                const value = inStage.reduce((sum, d) => sum + num(d.amount), 0);
                return (
                  <section
                    className={`column${dragging ? ' droppable' : ''}`}
                    key={s.key}
                    onDragOver={(e) => { if (dragging) e.preventDefault(); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const id = e.dataTransfer.getData('text/deal') || dragging;
                      setDragging(null);
                      if (id) void moveTo(id, s.key);
                    }}
                  >
                    <h2>{s.label} <span className="count">{inStage.length}</span>
                      <span className="colvalue">{money(value)}</span></h2>
                    {inStage.map((d) => {
                      const co = nameOf.get(d.company_id);
                      return (
                        <button
                          className="card dealcard" key={d.id}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/deal', d.id);
                            setDragging(d.id);
                          }}
                          onDragEnd={() => setDragging(null)}
                          onClick={() => setEditing(d)}
                        >
                          <header><h3>{d.name}</h3></header>
                          <p className="meta">{co?.name ?? '—'}</p>
                          <p className="dealamt">{money(d.amount)}
                            <span className="meta"> · {weightOf(d)}%</span></p>
                          {d.next_step && <p className="gap">{d.next_step}</p>}
                          <footer>
                            <span className="flag flag-mute">{shortDate(d.expected_close)}</span>
                          </footer>
                        </button>
                      );
                    })}
                  </section>
                );
              })}
            </div>
          ) : (
            <table className="grid">
              <thead>
                <tr><th>Deal</th><th>Account</th><th>Stage</th><th>Amount</th>
                  <th>%</th><th>Close</th><th>Next step</th></tr>
              </thead>
              <tbody>
                {rows.map((d) => {
                  const co = nameOf.get(d.company_id);
                  return (
                    <tr key={d.id}>
                      <td><button className="linkbtn" onClick={() => setEditing(d)}>{d.name}</button></td>
                      <td>{co ? <Link to={`/companies/${co.slug}`}>{co.name}</Link> : '—'}</td>
                      <td className="meta">{d.stage}</td>
                      <td>{money(d.amount)}</td>
                      <td className="meta">{weightOf(d)}%</td>
                      <td className="meta">{shortDate(d.expected_close)}</td>
                      <td className="meta">{d.next_step ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}

      {creating && <CreateMenu initial="deal"
        onClose={() => { setCreating(false); void reload(); }} />}
      {editing && (
        <EditDeal deal={editing} types={engagementTypes} onClose={() => setEditing(null)}
          onSave={(d) => update(editing.id, d)} />
      )}
    </>
  );
}
